import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Truck, 
  Plus, 
  Trash2, 
  Edit2, 
  Check,
  Phone,
  Mail,
  MapPin,
  Clock
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KATEGORIEN = [
  "Obst & Gemüse",
  "Fleisch & Wurst",
  "Fisch & Meeresfrüchte",
  "Milchprodukte",
  "Getränke",
  "Tiefkühlware",
  "Trockenwaren",
  "Gewürze",
  "Backwaren",
  "Non-Food",
  "Sonstiges"
];

export const Lieferanten = () => {
  const [lieferanten, setLieferanten] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLieferant, setEditingLieferant] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    kontakt_person: "",
    telefon: "",
    email: "",
    adresse: "",
    kategorie: "",
    mindestbestellwert: "",
    liefergebuehren: "",
    lieferzeit_tage: "",
    notizen: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/lieferanten`, { withCredentials: true });
      setLieferanten(response.data);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      kontakt_person: "",
      telefon: "",
      email: "",
      adresse: "",
      kategorie: "",
      mindestbestellwert: "",
      liefergebuehren: "",
      lieferzeit_tage: "",
      notizen: ""
    });
    setEditingLieferant(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (l) => {
    setEditingLieferant(l);
    setFormData({
      name: l.name,
      kontakt_person: l.kontakt_person || "",
      telefon: l.telefon || "",
      email: l.email || "",
      adresse: l.adresse || "",
      kategorie: l.kategorie || "",
      mindestbestellwert: l.mindestbestellwert?.toString() || "",
      liefergebuehren: l.liefergebuehren?.toString() || "",
      lieferzeit_tage: l.lieferzeit_tage?.toString() || "",
      notizen: l.notizen || ""
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name) {
      toast.error("Bitte Namen angeben");
      return;
    }

    try {
      const payload = {
        ...formData,
        mindestbestellwert: formData.mindestbestellwert ? parseFloat(formData.mindestbestellwert) : null,
        liefergebuehren: formData.liefergebuehren ? parseFloat(formData.liefergebuehren) : null,
        lieferzeit_tage: formData.lieferzeit_tage ? parseInt(formData.lieferzeit_tage) : null
      };

      if (editingLieferant) {
        await axios.put(`${API}/lieferanten/${editingLieferant.id}`, payload, { withCredentials: true });
        toast.success("Lieferant aktualisiert");
      } else {
        await axios.post(`${API}/lieferanten`, payload, { withCredentials: true });
        toast.success("Lieferant erstellt");
      }

      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Speichern");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Lieferant wirklich löschen?")) return;
    
    try {
      await axios.delete(`${API}/lieferanten/${id}`, { withCredentials: true });
      toast.success("Lieferant gelöscht");
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(value || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-[#858585]">Laden...</div>
      </div>
    );
  }

  return (
    <div data-testid="lieferanten-page">
      {/* Header */}
      <header className="page-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="page-title">Lieferanten</h1>
            <p className="page-subtitle">Lieferanten-Verwaltung und Kontakte</p>
          </div>
          <Button 
            onClick={openCreateDialog}
            className="bg-[#3A4D39] hover:bg-[#2C3B2B] text-white rounded-sm uppercase text-xs tracking-wide btn-press"
            data-testid="add-lieferant-btn"
          >
            <Plus size={16} className="mr-2" />
            Neuer Lieferant
          </Button>
        </div>
      </header>

      {/* Content */}
      {lieferanten.length === 0 ? (
        <div className="empty-state">
          <Truck size={64} className="mx-auto text-[#D1CFC9]" />
          <h3>Keine Lieferanten</h3>
          <p>Erfassen Sie Ihre Lieferanten für eine bessere Übersicht</p>
          <Button 
            onClick={openCreateDialog}
            className="mt-4 bg-[#3A4D39] hover:bg-[#2C3B2B] text-white rounded-sm"
          >
            <Plus size={16} className="mr-2" />
            Ersten Lieferanten erfassen
          </Button>
        </div>
      ) : (
        <div className="px-8 py-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {lieferanten.map((l) => (
              <div key={l.id} className="bg-white border border-stone-200 p-5" data-testid={`lieferant-card-${l.id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-display text-lg font-semibold text-[#1A1A1A]">{l.name}</h3>
                    {l.kategorie && (
                      <span className="badge badge-neutral mt-1">{l.kategorie}</span>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <button className="btn-icon" onClick={() => openEditDialog(l)}>
                      <Edit2 size={16} />
                    </button>
                    <button className="btn-icon danger" onClick={() => handleDelete(l.id)}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  {l.kontakt_person && (
                    <p className="text-[#5C5C5C]">
                      <span className="font-medium">Kontakt:</span> {l.kontakt_person}
                    </p>
                  )}
                  
                  {l.telefon && (
                    <p className="flex items-center gap-2 text-[#5C5C5C]">
                      <Phone size={14} />
                      <a href={`tel:${l.telefon}`} className="hover:text-[#3A4D39]">{l.telefon}</a>
                    </p>
                  )}
                  
                  {l.email && (
                    <p className="flex items-center gap-2 text-[#5C5C5C]">
                      <Mail size={14} />
                      <a href={`mailto:${l.email}`} className="hover:text-[#3A4D39] truncate">{l.email}</a>
                    </p>
                  )}
                  
                  {l.adresse && (
                    <p className="flex items-start gap-2 text-[#5C5C5C]">
                      <MapPin size={14} className="flex-shrink-0 mt-0.5" />
                      <span>{l.adresse}</span>
                    </p>
                  )}
                </div>

                {(l.mindestbestellwert || l.liefergebuehren || l.lieferzeit_tage) && (
                  <div className="mt-4 pt-4 border-t border-stone-100 flex gap-4 text-sm">
                    {l.mindestbestellwert && (
                      <div>
                        <p className="text-[#858585] text-xs">Mindestbestellung</p>
                        <p className="font-mono font-medium">{formatCurrency(l.mindestbestellwert)}</p>
                      </div>
                    )}
                    {l.liefergebuehren != null && l.liefergebuehren > 0 && (
                      <div>
                        <p className="text-[#858585] text-xs">Liefergebühren</p>
                        <p className="font-mono font-medium">{formatCurrency(l.liefergebuehren)}</p>
                      </div>
                    )}
                    {l.lieferzeit_tage && (
                      <div>
                        <p className="text-[#858585] text-xs">Lieferzeit</p>
                        <p className="font-medium flex items-center gap-1">
                          <Clock size={12} />
                          {l.lieferzeit_tage} Tag{l.lieferzeit_tage !== 1 ? 'e' : ''}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {l.notizen && (
                  <p className="mt-3 text-xs text-[#858585] italic">{l.notizen}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-xl bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              {editingLieferant ? "Lieferant bearbeiten" : "Neuer Lieferant"}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2">
                <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="z.B. Metro Cash & Carry"
                  className="border-0 border-b border-stone-300 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#3A4D39]"
                  data-testid="lieferant-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Kategorie</Label>
                <Select value={formData.kategorie} onValueChange={(v) => setFormData({...formData, kategorie: v})}>
                  <SelectTrigger className="border-stone-300 rounded-sm">
                    <SelectValue placeholder="Auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {KATEGORIEN.map((k) => (
                      <SelectItem key={k} value={k}>{k}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Kontaktperson</Label>
                <Input
                  value={formData.kontakt_person}
                  onChange={(e) => setFormData({...formData, kontakt_person: e.target.value})}
                  placeholder="z.B. Herr Müller"
                  className="border-stone-300 rounded-sm"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Telefon</Label>
                <Input
                  value={formData.telefon}
                  onChange={(e) => setFormData({...formData, telefon: e.target.value})}
                  placeholder="z.B. 089 12345678"
                  className="border-stone-300 rounded-sm"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">E-Mail</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="z.B. kontakt@lieferant.de"
                  className="border-stone-300 rounded-sm"
                />
              </div>

              <div className="col-span-2 space-y-2">
                <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Adresse</Label>
                <Input
                  value={formData.adresse}
                  onChange={(e) => setFormData({...formData, adresse: e.target.value})}
                  placeholder="z.B. Industriestr. 1, 80000 München"
                  className="border-stone-300 rounded-sm"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Mindestbestellwert (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.mindestbestellwert}
                  onChange={(e) => setFormData({...formData, mindestbestellwert: e.target.value})}
                  placeholder="z.B. 100"
                  className="border-stone-300 rounded-sm font-mono"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Liefergebühren (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.liefergebuehren}
                  onChange={(e) => setFormData({...formData, liefergebuehren: e.target.value})}
                  placeholder="z.B. 4.90"
                  className="border-stone-300 rounded-sm font-mono"
                  data-testid="liefergebuehren-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Lieferzeit (Tage)</Label>
                <Input
                  type="number"
                  value={formData.lieferzeit_tage}
                  onChange={(e) => setFormData({...formData, lieferzeit_tage: e.target.value})}
                  placeholder="z.B. 2"
                  className="border-stone-300 rounded-sm font-mono"
                />
              </div>

              <div className="col-span-2 space-y-2">
                <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Notizen</Label>
                <Textarea
                  value={formData.notizen}
                  onChange={(e) => setFormData({...formData, notizen: e.target.value})}
                  placeholder="Zusätzliche Informationen..."
                  className="border-stone-300 rounded-sm resize-none"
                  rows={3}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-stone-100">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">
                Abbrechen
              </Button>
              <Button type="submit" className="bg-[#3A4D39] hover:bg-[#2C3B2B] text-white rounded-sm btn-press">
                <Check size={16} className="mr-2" />
                {editingLieferant ? "Aktualisieren" : "Erstellen"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Lieferanten;
